import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient, User, CreateUserDto, UpdateUserDto, UserRole, Tenant } from '../../services/api';
import { Users as UsersIcon, Plus, Edit2, Trash2, ArrowLeft, Search, Filter } from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ConfirmDialog, { useConfirmDialog } from '../../components/ui/ConfirmDialog';

export default function UserManagement() {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [feedback, setFeedback] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();

  // Verificar permissão
  const hasPermission = user?.role === 'super_admin' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';

  // Redirecionar se não tiver permissão (via useEffect para evitar erros de render)
  useEffect(() => {
    if (user && !hasPermission) {
      navigate('/waba');
    }
  }, [user, hasPermission, navigate]);

  useEffect(() => {
    if (hasPermission) {
      loadTenants();
    }
  }, [hasPermission]);

  useEffect(() => {
    // Backend requer tenantId para super_admin e admin regular
    // Só carrega quando tenantFilter está definido e não é 'all'
    if (hasPermission && tenantFilter && tenantFilter !== 'all') {
      loadUsers();
    }
  }, [tenantFilter, hasPermission, roleFilter]);

  const loadTenants = async () => {
    if (!accessToken) return;

    try {
      // Admin regular só vê seu próprio tenant
      // O user do login retorna tenantId diretamente (não tenant.id)
      const userTenantId = user?.tenantId || user?.tenant?.id;

      if (!isSuperAdmin) {
        if (userTenantId) {
          // Create a minimal tenant object for display
          const tenantName = user?.tenant?.name || 'Minha Empresa';
          setTenants([{ id: userTenantId, name: tenantName } as Tenant]);
          setTenantFilter(userTenantId);
        } else {
          // Se não tiver tenantId no user, buscar do backend via profile
          const profileResponse = await apiClient.getProfile(accessToken);
          if (profileResponse.success && profileResponse.data?.user?.tenantId) {
            // Primeiro verifica tenantId direto
            const tenantId = profileResponse.data.user.tenantId;
            const tenantName = profileResponse.data.user.tenant?.name || 'Minha Empresa';
            setTenants([{ id: tenantId, name: tenantName } as Tenant]);
            setTenantFilter(tenantId);
          } else if (profileResponse.success && profileResponse.data?.user?.tenant?.id) {
            const tenant = profileResponse.data.user.tenant;
            setTenants([{ id: tenant.id, name: tenant.name || 'Minha Empresa' } as Tenant]);
            setTenantFilter(tenant.id);
          }
        }
        return;
      }

      // Super admin vê todos os tenants
      const response = await apiClient.getTenants(
        { page: 1, limit: 100 },
        accessToken
      );

      if (response.success && response.data?.data) {
        setTenants(response.data.data);
        // Set first tenant as default filter
        if (response.data.data.length > 0) {
          setTenantFilter(response.data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar tenants:', error);
    }
  };

  const loadUsers = async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      const params = {
        page: 1,
        limit: 100,
        ...(roleFilter !== 'all' && { role: roleFilter }),
        tenantId: tenantFilter, // Sempre passa o tenantId (backend requer)
      };

      const response = await apiClient.getUsers(params, accessToken);

      if (response.success) {
        // Backend retorna { success: true, data: User[] }
        // O tipo espera UserListResponse { data: User[], meta: {...} }
        // Tratar ambos os formatos
        const usersData = response.data;

        if (Array.isArray(usersData)) {
          // Resposta é diretamente um array de usuários
          setUsers(usersData);
        } else if (usersData?.data && Array.isArray(usersData.data)) {
          // Resposta tem estrutura paginada { data: User[], meta: {...} }
          setUsers(usersData.data);
        } else {
          setUsers([]);
        }
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (data: CreateUserDto) => {
    if (!accessToken) return;

    try {
      const response = await apiClient.createUser(data, accessToken);
      if (response.success) {
        setShowCreateModal(false);
        loadUsers();
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      setFeedback({ type: 'error', message: 'Erro ao criar usuário. Verifique os dados e tente novamente.' });
    }
  };

  const handleUpdateUser = async (id: string, data: UpdateUserDto) => {
    if (!accessToken) return;

    try {
      // Pass tenantFilter if it's not 'all'
      const tenantId = tenantFilter !== 'all' ? tenantFilter : undefined;
      const response = await apiClient.updateUser(id, data, accessToken, tenantId);
      if (response.success) {
        setShowEditModal(false);
        setSelectedUser(null);
        loadUsers();
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      setFeedback({ type: 'error', message: 'Erro ao atualizar usuário. Tente novamente.' });
    }
  };

  const handleDeleteUser = (id: string) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Deletar usuário',
      message: 'Tem certeza que deseja deletar este usuário? Esta ação é irreversível.',
      variant: 'danger',
      confirmText: 'Deletar',
      onConfirm: async () => {
        try {
          // Pass tenantFilter if it's not 'all'
          const tenantId = tenantFilter !== 'all' ? tenantFilter : undefined;
          await apiClient.deleteUser(id, accessToken, tenantId);
          loadUsers();
        } catch (error) {
          console.error('Erro ao deletar usuário:', error);
          setFeedback({ type: 'error', message: 'Erro ao deletar usuário. Tente novamente.' });
        }
      },
    });
  };

  const filteredUsers = (users || []).filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === 'all' || u.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  // Check permissions - show access denied if not authorized
  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-bg-1 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-1 mb-2">Acesso Negado</h1>
          <p className="text-text-2">Você não tem permissão para acessar esta página.</p>
        </div>
      </div>
    );
  }

  const getRoleBadge = (role: UserRole) => {
    const colors = {
      super_admin: 'bg-accent-2/10 text-accent-2',
      admin: 'bg-info/10 text-accent-1',
      user: 'bg-success/10 text-success',
      viewer: 'bg-bg-3 text-text-1',
    };

    const labels = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      user: 'Usuário',
      viewer: 'Visualizador',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role]}`}>
        {labels[role]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-bg-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-text-2 hover:text-text-1 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Dashboard
          </button>

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-text-1 flex items-center">
                <UsersIcon className="w-6 h-6 mr-2" />
                Gerenciar Usuários
              </h1>
              <p className="mt-1 text-sm text-text-2">
                Total: {filteredUsers.length} usuário(s)
              </p>
            </div>

            {isSuperAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Usuário
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-bg-2 rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-3 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
              />
            </div>

            {isSuperAdmin && (
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-3 w-5 h-5" />
                <select
                  value={tenantFilter}
                  onChange={(e) => setTenantFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                >
                  <option value="all">Todas as empresas</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-3 w-5 h-5" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')}
                className="w-full pl-10 pr-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
              >
                <option value="all">Todos os papéis</option>
                <option value="admin">Admin</option>
                <option value="user">Usuário</option>
              </select>
            </div>
          </div>
        </div>

        {/* Feedback Banner */}
        {feedback && (
          <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
            feedback.type === 'error'
              ? 'bg-danger/10 border border-danger/30 text-danger'
              : 'bg-success/10 border border-success/30 text-success'
          }`}>
            <span className="text-sm">{feedback.message}</span>
            <button onClick={() => setFeedback(null)} className="ml-2 hover:opacity-70">
              &times;
            </button>
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <LoadingSpinner text="Carregando..." />
        ) : (
          <div className="bg-bg-2 rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-border-1">
              <thead className="bg-bg-2">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                    Papel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                    Criado em
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-bg-1 divide-y divide-border-1">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-text-3">
                      Nenhum usuário encontrado
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-bg-3/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-text-1">
                            {u.name}
                          </div>
                          <div className="text-sm text-text-3">{u.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(u.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.active
                              ? 'bg-success/10 text-success'
                              : 'bg-bg-3 text-text-1'
                          }`}
                        >
                          {u.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-text-3">
                          {new Date(u.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedUser(u);
                            setShowEditModal(true);
                          }}
                          className="text-accent-1 hover:text-accent-1/80 mr-4"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="text-danger hover:text-danger/80"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateUserModal
          tenants={tenants}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSubmit={(data) => handleUpdateUser(selectedUser.id, data)}
        />
      )}

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}

// Create Modal Component
function CreateUserModal({
  tenants,
  isSuperAdmin,
  onClose,
  onSubmit,
}: {
  tenants: Tenant[];
  isSuperAdmin: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserDto) => void;
}) {
  const [formData, setFormData] = useState<CreateUserDto>({
    name: '',
    email: '',
    password: '',
    role: 'user',
    tenantId: (tenants && tenants.length > 0) ? tenants[0].id : '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-bg-1 rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Criar Novo Usuário</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Nome *
            </label>
            <input
              type="text"
              required
              minLength={3}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Senha *
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Papel
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            >
              <option value="user">Usuário</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {isSuperAdmin && (
            <div>
              <label className="block text-sm font-medium text-text-2 mb-1">
                Empresa *
              </label>
              <select
                value={formData.tenantId}
                onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                required
              >
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border-1 rounded-lg hover:bg-bg-3/50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
            >
              Criar Usuário
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Modal Component
function EditUserModal({
  user,
  onClose,
  onSubmit,
}: {
  user: User;
  onClose: () => void;
  onSubmit: (data: UpdateUserDto) => void;
}) {
  const [formData, setFormData] = useState<UpdateUserDto>({
    name: user.name,
    email: user.email,
    role: user.role,
    active: user.active,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-bg-1 rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Editar Usuário</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Nome
            </label>
            <input
              type="text"
              minLength={3}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Nova Senha (deixe em branco para não alterar)
            </label>
            <input
              type="password"
              minLength={8}
              value={formData.password || ''}
              onChange={(e) => setFormData({ ...formData, password: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-2 mb-1">
              Papel
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
            >
              <option value="user">Usuário</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="w-4 h-4 text-accent-1 border-border-1 rounded focus:ring-accent-1"
            />
            <label htmlFor="active" className="ml-2 text-sm text-text-2">
              Usuário ativo
            </label>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border-1 rounded-lg hover:bg-bg-3/50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
            >
              Salvar Alterações
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
